import React from 'react';

import CalendarIcon from 'icons/calendar.svg';
import DesktopMobileIcon from 'icons/desktop-mobile.svg';
import AutoEnrolIcon from 'icons/double-arrow-rotate.svg';
import LaunchIcon from 'icons/launch.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import ActionsPill from 'components/application/ActionsPill';

import { useCurrentActor } from 'components/contexts/CurrentActor';

import { formatDate } from 'components/helpers/dates';
import {
  autoEnrolmentNonCriticalBlockersStatusText,
  hasOnlyNonCriticalBlockers,
  proposedAutoEnrolmentDate,
} from 'components/helpers/resources/autoEnrolments';
import { resourceShape } from 'components/helpers/serialisableResources';
import { personDisplayName } from 'components/helpers/users';

export default function ELearningBookings({
  booker,
  booking,
  course,
  currentAutoEnrolmentExclusionUserSelection,
  currentPersonnel,
  currentTrainingStatus,
  eLearningCourse,
  isCreditInfoUnavailable,
  isELearningLaunchable,
  isEligibleForManualBooking,
  latestTraining,
  onAddELearningBooking,
  prioritisedAutoEnrolmentBlockers,
  registration,
}) {
  const currentActor = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'training.ELearningBookings',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const { t: i18nTHelpers } = useTranslation('components', {
    keyPrefix: 'helpers.resources.autoEnrolments',
  });

  const isPersonnelEmailUnavailable = !currentPersonnel.email;

  const isELearningNonCustom =
    eLearningCourse && !eLearningCourse.attributes.custom;

  let bookingTooltip;

  if (isELearningNonCustom && isCreditInfoUnavailable) {
    bookingTooltip = i18nTCommon('bookings.errors.creditInfoUnavailable');
  } else if (isPersonnelEmailUnavailable) {
    bookingTooltip = i18nTCommon('personnel.errors.mustHaveEmailForELearning');
  } else {
    bookingTooltip = {
      expired: i18nTCommon('training.trainingIsExpiredAndRequiresBooking'),
      expiring: i18nTCommon(
        'training.trainingIsExpiringSoonAndRequiresBooking',
      ),
      missing: i18nTCommon('training.trainingIsMissingAndRequiresBooking'),
    }[currentTrainingStatus];
  }

  const enrolmentTooltipText = ({ booker, booking, registration }) => {
    let tooltipText = '';

    const { autoEnrolled: isAutoEnrolled, createdAt } = booking.attributes;

    const formattedCreatedAt =
      createdAt && formatDate({ date: createdAt, format: 'd MMM yy' });

    const lastAccessedDate =
      registration &&
      formatDate({
        date: registration.attributes.updatedAt,
        format: 'd MMM yy',
      });

    if (isAutoEnrolled) {
      tooltipText = i18nT(
        `enrolmentTooltip.${registration ? 'autoEnrolledWithRegistration' : 'autoEnrolled'}`,
        { date: formattedCreatedAt, lastAccessedDate },
      );
    } else if (booker) {
      tooltipText = i18nT(
        `enrolmentTooltip.${registration ? 'bookerWithRegistration' : 'booker'}`,
        {
          date: formattedCreatedAt,
          interpolation: { escapeValue: false },
          name: [
            personDisplayName({ attributes: booker.attributes }),
            booker.attributes.deletedAt && i18nTCommon('shared.deletedUser'),
          ]
            .filter(Boolean)
            .join(' '),
          lastAccessedDate,
        },
      );
    } else {
      tooltipText = i18nT(
        `enrolmentTooltip.${registration ? 'defaultWithRegistration' : 'default'}`,
        { date: formattedCreatedAt, lastAccessedDate },
      );
    }

    return tooltipText;
  };

  if (booking && isELearningLaunchable) {
    return (
      <ActionsPill
        actionIcon={<LaunchIcon className='tw-m-1' height={16} width={16} />}
        color='cyan'
        displayText={
          registration ?
            i18nTCommon('courses.continueELearning')
          : i18nTCommon('courses.startELearning')
        }
        icon={<DesktopMobileIcon height={16} width={16} />}
        onClick={() =>
          window.open(
            `/e_learning/bookings/${booking.attributes.hashid}`,
            '_blank',
          )
        }
      />
    );
  } else {
    if (registration) {
      return (
        <ActionsPill
          color='green'
          displayText={i18nTCommon('bookings.eLearningInProgress')}
          icon={<CalendarIcon height={16} width={16} />}
          tooltipText={enrolmentTooltipText({ booking, booker, registration })}
        />
      );
    }

    if (booking) {
      return (
        <ActionsPill
          color='green'
          displayText={i18nTCommon('bookings.eLearningEnrolled')}
          icon={<CalendarIcon height={16} width={16} />}
          tooltipText={enrolmentTooltipText({ booking, booker, registration })}
        />
      );
    }
  }

  if (
    currentActor.isAllowedFeature([
      'e_learning_auto_enrol',
      'e_learning',
      'training_register',
    ]) &&
    !currentAutoEnrolmentExclusionUserSelection
  ) {
    const hasNoBlockers = prioritisedAutoEnrolmentBlockers.length === 0;
    const hasNonCriticalBlockers = hasOnlyNonCriticalBlockers({
      prioritisedAutoEnrolmentBlockers,
    });

    if (hasNoBlockers || hasNonCriticalBlockers) {
      const proposedEnrolmentDate = proposedAutoEnrolmentDate({
        courseExpiringDurationParts:
          course?.attributes.expiringDurationParts || {},
        latestTraining,
      });

      return (
        <ActionsPill
          color={hasNonCriticalBlockers ? 'amber' : 'green'}
          displayText={i18nTCommon('courses.autoEnrols')}
          icon={<AutoEnrolIcon height={16} width={16} />}
          tooltipText={autoEnrolmentNonCriticalBlockersStatusText({
            priorityBlocker: prioritisedAutoEnrolmentBlockers[0],
            proposedEnrolmentDate,
            translations: i18nTHelpers(
              'autoEnrolmentNonCriticalBlockersStatusText',
              {
                date:
                  proposedEnrolmentDate &&
                  formatDate({
                    date: proposedEnrolmentDate,
                    format: 'd MMM yy',
                  }),
                returnObjects: true,
              },
            ),
          })}
        />
      );
    }
  }

  if (isEligibleForManualBooking)
    return (
      <ActionsPill
        color='cyan'
        displayText={i18nTCommon('bookings.enrolELearning')}
        icon={<CalendarIcon height={16} width={16} />}
        isDisabled={isPersonnelEmailUnavailable || isCreditInfoUnavailable}
        onClick={() => onAddELearningBooking({ course, eLearningCourse })}
        tooltipText={bookingTooltip}
      />
    );

  return null;
}

ELearningBookings.propTypes = {
  booking: PropTypes.object,
  booker: resourceShape('simpleUser'),
  course: PropTypes.object,
  eLearningCourse: resourceShape('eLearningCourse'),
  registration: PropTypes.object,
  latestTraining: PropTypes.object,
  currentTrainingStatus: PropTypes.string,
  currentPersonnel: PropTypes.object,
  isCreditInfoUnavailable: PropTypes.bool,
  isEligibleForManualBooking: PropTypes.bool,
  prioritisedAutoEnrolmentBlockers: PropTypes.array,
  currentAutoEnrolmentExclusionUserSelection: PropTypes.bool,
  isELearningLaunchable: PropTypes.bool,
  onAddELearningBooking: PropTypes.func,
};

ELearningBookings.defaultProps = {
  hasPersonnelEditableAccess: true,
};
